/*
**  rui-button.js
**
**  rui-button component class
*/
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ruiCommonStyles } from '../common/rui-common-styles.js';
import { ruiCommonStylesButtons } from '../common/rui-common-styles-buttons.js';
export class RuiButton extends LitElement {
    static get is() {
        return 'rui-button';
    }
    static get properties() {
        return {
            text: { type: String },
            iconType: { type: String },
            buttonStyle: { type: String },
            buttonSize: { type: String },
            buttonType: { type: String },
            badge: { type: String },
            isDisabled: { type: Object },
            context: { type: String }
        };
    }
    constructor() {
        super();
        this.text = '';
        this.iconType = '';
        this.buttonStyle = '';
        this.buttonSize = 'medium';
        this.buttonType = 'button';
        this.badge = '';
        this.isDisabled = false;
        this.context = '';
        this._form = null;
        this._submitButton = null;
        this._submitEvent = new CustomEvent('submit', {
            composed: true,
            bubbles: true,
            cancelable: true,
        });
        this._eventListenersAdded = false;
        this._hasCustomIcon = false;
    }
    connectedCallback() {
        super.connectedCallback();
        // Adding timeout to ensure all buttonTypes are updated
        window.setTimeout(() => {
            // If this is a submit button and there is a parent form, add an event listener to the form and listen for enter key.
            this._form = this.closest('form');
            if (this._form && this.buttonType.toLowerCase() === 'submit') {
                // Create submit button element and append to form so typical Enter keydown submits form.
                this._submitButton = document.createElement('button');
                this._submitButton.setAttribute('type', 'submit');
                this._submitButton.setAttribute('class', 'sr-only');
                this._form.appendChild(this._submitButton);
                // Add listener to form.
                this._form.addEventListener('submit', this.onFormSubmit.bind(this));
                this._eventListenersAdded = true;
            }
        }, 10);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._eventListenersAdded) {
            this._form.removeEventListener('submit', this.onFormSubmit.bind(this));
            this._eventListenersAdded = false;
        }
    }
    click(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDisabled) {
            if (this._form && this.buttonType === 'submit') {
                // If this is a submit button and there's a parent form, dispatch custom submit event
                this._form.dispatchEvent(this._submitEvent);
            }
            else {
                // Otherwise dispatch rui-click event
                let clickEvent = new CustomEvent('rui-click', {
                    detail: {},
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(clickEvent);
            }
        }
    }
    // On form submit, prevent default action
    onFormSubmit(e) {
        e.preventDefault();
    }
    render() {
        const buttonStyle = this.buttonStyle.toLowerCase();
        let styleClassName = '';
        let iconContext = '';
        const context = this.context.toLowerCase();
        if (buttonStyle === 'primary') {
            styleClassName = 'rui-btn-primary';
            iconContext = 'primary-btn';
        }
        else if (buttonStyle === 'destructive') {
            styleClassName = 'rui-btn-destructive';
            iconContext = 'destructive-btn';
        }
        else {
            styleClassName = 'rui-btn-secondary';
            iconContext = 'secondary-btn';
        }
        if (this.isDisabled) {
            iconContext = 'disabled-btn';
        }
        if (this.querySelector(`*[slot='custom-icon']`)) {
            this._hasCustomIcon = true;
        }
        const buttonClasses = {
            ['rui-btn']: true,
            ['rui-btn-icon-only']: (this.iconType || this._hasCustomIcon) && !this.text,
            ['rui-btn-with-badge']: this.badge,
            ['rui-btn-context-' + context]: context,
            [styleClassName]: true,
            ['rui-btn-sm']: this.buttonSize.toLowerCase() === 'small'
        };
        const badgeClasses = {
            ['rui-btn-badge']: true,
            ['rui-btn-badge-disabled']: this.isDisabled
        };
        return html `
            <button class="${classMap(buttonClasses)}" ?disabled="${this.isDisabled}" type="${this.buttonType}" @click="${this.click}">
                ${this.iconType && !this.text ? html `<span class="sr-only">${this.iconType}</span>` : ''}
                ${this.iconType || this._hasCustomIcon ? html `<rui-icon iconType="${this.iconType}" context="${iconContext}" class="rui-btn-icon" aria-hidden="true"><slot name="custom-icon"></slot></rui-icon>` : ''}
                ${this.text ? html `<span class="rui-btn-text">${this.text}</span>` : ''}
                ${this.badge ? html `<span class="${classMap(badgeClasses)}">${this.badge}</span>` : ''}
            </button>
        `;
    }
    static get styles() {
        return [
            ruiCommonStyles,
            ruiCommonStylesButtons,
            css `
                .rui-btn-badge {
                    border-radius: var(--rui-btn-badge-border-radius, 10px);
                    border-style: var(--rui-btn-badge-border-style, solid);
                    border-width: var(--rui-btn-badge-border-width, 1px);
                    display: inline-block;
                    font-size: var(--rui-btn-badge-font-size, 12px);
                    font-weight: var(--rui-btn-badge-font-weight, 700);
                    line-height: var(--rui-btn-badge-line-height, 1);
                    min-width: var(--rui-btn-badge-min-width, 10px);
                    padding: var(--rui-btn-badge-padding, 2px 6px 4px 6px);
                    position: absolute;
                    right: var(--rui-btn-badge-right, -12px);
                    text-align: center;
                    top: var(--rui-btn-badge-top, -10px);
                    vertical-align: middle;
                    white-space: nowrap;
                }

                .rui-btn-primary .rui-btn-badge {
                    background-color: var(--rui-btn-badge-bg-primary, #085ce5);
                    border-color: var(--rui-content-primary-bg, #fff);
                    color: var(--rui-btn-badge-fg-primary, #ffffff);
                }

                .rui-btn-secondary .rui-btn-badge {
                    background-color: var(--rui-btn-badge-bg-secondary, #085ce5);
                    border-color: var(--rui-content-primary-bg, #fff);
                    color: var(--rui-btn-badge-fg-secondary, #ffffff);
                }

                .rui-btn-destructive .rui-btn-badge {
                    background-color: var(--rui-btn-badge-bg-destructive, #e13329);
                    border-color: var(--rui-content-primary-bg, #fff);
                    color: var(--rui-btn-badge-fg-destructive, #ffffff);
                }

                .rui-btn-primary .rui-btn-badge.rui-btn-badge-disabled,
                .rui-btn-destructive .rui-btn-badge.rui-btn-badge-disabled,
                .rui-btn-secondary .rui-btn-badge.rui-btn-badge-disabled {
                    color: var(--rui-btn-primary-fg-disabled, #999);
                    background-color: var(--rui-btn-primary-bg-disabled, #e9e9e9);
                    border-color: var(--rui-btn-primary-border-disabled, #cdcdcd);
                }

                .rui-btn-badge:empty {
                    display: none;
                }

                .rui-btn-with-badge {
                    margin-right: 12px;
                    width: calc(100% - 12px);
                }

                .rui-btn-context-action-button {
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-color: var(--rui-line, #e0e8f5);
                    margin-left: -1px;
                }

                .rui-btn-context-action-button-error {
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-color: var(--rui-text-error, #e13329);
                    margin-left: -1px;
                }

                .rui-btn-icon {
                    position: relative;
                    top: 1px;
                }

                .rui-btn-icon + .rui-btn-text {
                    margin-left: 2px
                }

                .rui-btn-icon-only:not(.rui-btn-context-action-button) {
                    min-width: 32px;
                    padding: 5px;
                }

                .rui-btn-sm.rui-btn-icon-only {
                    min-width: 30px;
                }

                @media print {
                    .rui-btn .rui-btn-badge {
                        background-color: var(--rui-content-primary-bg, #fff);
                        border-color: var(--rui-text-regular, #09175b);
                        color: var(--rui-text-regular, #09175b);
                    }

                    .rui-btn:disabled .rui-btn-badge {
                        opacity: 0.66;
                    }

                    .rui-btn-context-action-button {
                        border-color: #000000;
                    }

                    .rui-btn-context-action-button-error {
                        border-color: #FF0000;
                    }
                }
            `
        ];
    }
}
RuiButton.shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
customElements.get(RuiButton.is) || customElements.define(RuiButton.is, RuiButton);
